import { useCallback, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { Plus } from 'lucide-react';
import { ME_QUERY } from '../graphql/queries';
import { Header } from './Header';
import { SideNav } from './SideNav';
import { BottomTabBar } from './BottomTabBar';
import { DesktopSidebar } from './DesktopSidebar';
import { CaptureSheet } from './CaptureSheet';
import { SearchOverlay } from './SearchOverlay';
import { useOutboxSync } from '../hooks/useOutbox';

interface LayoutProps {
  children: React.ReactNode;
}

interface User {
  id: string;
  email: string;
  displayName?: string;
}

interface MeData {
  me: User | null;
}

/**
 * Main app shell: fixed header (wordmark, search, mobile menu), the mobile
 * bottom tab bar, the desktop sidebar, the slide-out drawer, the "Tell me"
 * capture FAB, and the document search overlay.
 */
export function Layout({ children }: LayoutProps) {
  const [sideNavOpen, setSideNavOpen] = useState(false);
  const [showCaptureSheet, setShowCaptureSheet] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const captureFabRef = useRef<HTMLButtonElement>(null);
  const captureTriggerRef = useRef<Element | null>(null);
  const searchTriggerRef = useRef<Element | null>(null);
  const { pathname } = useLocation();

  const { data } = useQuery<MeData>(ME_QUERY);
  const user = data?.me ?? undefined;

  // Replay offline document saves whenever connectivity plausibly returned
  // (startup, `online`, foregrounding, slow retry) — see lib/outbox.ts.
  useOutboxSync();

  // The FAB hides where it duplicates or occludes a page's own controls
  // (design review A2): the documents area manages documents directly, the
  // /alma chat (including /alma/:sessionId) IS the composer, and Spaces has
  // nothing for it to add.
  const showFab =
    pathname !== '/docs' &&
    !pathname.startsWith('/docs/') &&
    pathname !== '/alma' &&
    !pathname.startsWith('/alma/') &&
    pathname !== '/spaces';

  // Capture the trigger (FAB, in-page button…) as the sheet opens so close can
  // hand focus back to it; the FAB is the fallback.
  const openCapture = useCallback(() => {
    captureTriggerRef.current = document.activeElement;
    setShowCaptureSheet(true);
  }, []);

  const closeCaptureSheet = useCallback(() => {
    setShowCaptureSheet(false);
    const trigger = captureTriggerRef.current as HTMLElement | null;
    captureTriggerRef.current = null;
    if (trigger && document.contains(trigger) && typeof trigger.focus === 'function') {
      trigger.focus();
    } else {
      captureFabRef.current?.focus();
    }
  }, []);

  const openSearch = useCallback(() => {
    searchTriggerRef.current = document.activeElement;
    setSearchOpen(true);
  }, []);

  const closeSearch = useCallback(() => {
    setSearchOpen(false);
    const trigger = searchTriggerRef.current as HTMLElement | null;
    searchTriggerRef.current = null;
    if (trigger && document.contains(trigger) && typeof trigger.focus === 'function') {
      trigger.focus();
    }
  }, []);

  return (
    <div className="min-h-screen text-[var(--text)]">
      <div className="fixed top-0 left-0 right-0 z-40 mobile-header">
        <Header
          showMenuButton
          onMenuClick={() => setSideNavOpen(true)}
          onSearchClick={openSearch}
        />
      </div>

      <SideNav isOpen={sideNavOpen} onClose={() => setSideNavOpen(false)} user={user} />
      <DesktopSidebar user={user} />

      <main className="pt-16 main-content-mobile main-bottom-clearance lg:pl-64">
        {/* Keyed on pathname so route changes get the quick fade-in
            (reduced-motion drops it via the animation class). */}
        <div key={pathname} className="p-4 lg:p-8 max-w-6xl mx-auto animate-page-in">
          {children}
        </div>
      </main>

      <BottomTabBar />

      {/* Global FAB — raised above the tab bar on mobile, larger for easy tap */}
      {showFab && (
        <button
          ref={captureFabRef}
          type="button"
          onClick={openCapture}
          className="fab fixed bottom-[calc(4.75rem+env(safe-area-inset-bottom))] right-4 lg:bottom-12 lg:right-8 z-40 flex items-center gap-2.5 min-h-[3.25rem] pl-5 pr-5 py-3 sm:pl-6 sm:pr-6 sm:py-4 bg-gradient-to-r from-[var(--marine-600)] to-[var(--marine-400)] text-white rounded-full shadow-lg hover:shadow-xl transition-all active:scale-95"
          aria-label="Tell me"
        >
          <Plus className="w-6 h-6 shrink-0" strokeWidth={2.5} />
          <span className="hidden sm:inline text-base font-semibold whitespace-nowrap">
            Tell me
          </span>
        </button>
      )}

      {showCaptureSheet && <CaptureSheet onClose={closeCaptureSheet} />}
      {searchOpen && <SearchOverlay onClose={closeSearch} />}
    </div>
  );
}
